import { toast } from 'sonner';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import api from '../api';

function AdminLenders() {
  const [lenders, setLenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLender, setEditingLender] = useState(null);
  const [creating, setCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    doc_type: 'DNI',
    doc_number: '',
    address_full: '',
    country: 'España',
    phone: '',
    iban: '',
    language_pref: 'es'
  });
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => { // eslint-disable-line react-hooks/exhaustive-deps
    fetchLenders();
  }, []);

  const fetchLenders = async () => {
    try {
      const response = await api.get('/api/admin/lenders', {
        params: searchTerm ? { search: searchTerm } : {}
      });
      setLenders(response.data);
    } catch (error) {
      console.error('Error fetching lenders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setLoading(true);
    fetchLenders();
  };

  const handleCreateLender = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      if (showEditModal && editingLender) {
        await api.put(`/api/admin/lenders/${editingLender.user._id}`, {
          user_data: {
            name: formData.name,
            surname: formData.surname,
            email: formData.email,
            language_pref: formData.language_pref
          },
          profile_data: {
            doc_type: formData.doc_type,
            doc_number: formData.doc_number,
            address_full: formData.address_full,
            country: formData.country,
            phone: formData.phone,
            iban: formData.iban
          }
        });

        toast.success(t('messages.lenderUpdated'));
        setShowEditModal(false);
        setEditingLender(null);
      } else {
        const response = await api.post('/api/admin/lenders', {
          user_data: {
            name: formData.name,
            surname: formData.surname,
            email: formData.email,
            password: 'temp123',
            role: 'prestamista',
            language_pref: formData.language_pref
          },
          profile_data: {
            user_id: '',
            doc_type: formData.doc_type,
            doc_number: formData.doc_number,
            address_full: formData.address_full,
            country: formData.country,
            phone: formData.phone,
            iban: formData.iban
          }
        });

        toast.success(`${t('messages.lenderCreated')}\\n\\nEmail: ${formData.email}\\nContraseña temporal: ${response.data.temp_password}\\n\\n📧 Email enviado.`);
        closeModal();  // Use closeModal for consistent state cleanup
      }
      
      fetchLenders();
    } catch (error) {
      toast.error(t('common.error') + ': ' + (error.response?.data?.detail || t('errors.unknown')));
    } finally {
      setCreating(false);
    }
  };

  const openEditModal = (lender) => {
    setEditingLender(lender);
    setFormData({
      name: lender.user.name,
      surname: lender.user.surname,
      email: lender.user.email,
      doc_type: lender.profile?.doc_type || 'DNI',
      doc_number: lender.profile?.doc_number || '',
      address_full: lender.profile?.address_full || '',
      country: lender.profile?.country || 'España',
      phone: lender.profile?.phone || '',
      iban: lender.profile?.iban || '',
      language_pref: lender.user.language_pref || 'es'
    });
    setShowEditModal(true);
  };

  const handleResetPassword = async (userId, userName) => {
    if (!confirm(`¿Resetear contraseña de ${userName}?`)) return;

    try {
      const response = await api.post(`/api/admin/reset-user-password/${userId}`);
      toast.success(`Contraseña restablecida correctamente\n\nEmail: ${response.data.user_email}\nContraseña temporal: ${response.data.temp_password}\n\n⚠️ Comunica esta contraseña al usuario de forma segura`);
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || 'Error desconocido'));
    }
  };

  const handleDeleteLender = async (userId, userName, userEmail) => {
    const confirmation = prompt(
      `⚠️ ELIMINAR PRESTAMISTA\n\n` +
      `Esta acción es PERMANENTE y eliminará:\n` +
      `- Cuenta de usuario\n` +
      `- Perfil de prestamista\n` +
      `- Préstamos pendientes\n` +
      `- Contratos no firmados\n` +
      `- Solicitudes de préstamo\n` +
      `- Documentos asociados\n\n` +
      `Prestamista: ${userName}\n` +
      `Email: ${userEmail}\n\n` +
      `NOTA: No se puede eliminar si tiene préstamos ACTIVOS.\n\n` +
      `Para confirmar, escribe: ELIMINAR`
    );

    if (confirmation !== 'ELIMINAR') {
      if (confirmation !== null) {
        toast.info(t('misc.deleteConfirm'));
      }
      return;
    }

    try {
      const response = await api.delete(`/api/admin/lenders/${userId}`);
      toast.success(`Prestamista eliminado correctamente`);
      fetchLenders(); // Refresh list
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error desconocido';
      toast.error(`Error al eliminar prestamista:\n\n${errorMsg}`);
    }
  };

  const handleAddInvitations = async (userId, userName, currentAvailable, currentUsed) => {
    const number = prompt(
      `➕ AFEGIR INVITACIONS\n\n` +
      `Prestamista: ${userName}\n` +
      `Invitacions disponibles: ${currentAvailable}\n` +
      `Invitacions utilitzades: ${currentUsed}\n\n` +
      `Quantes invitacions vols afegir? (1-10)`
    );

    if (!number || number === null) return;

    const additional = parseInt(number);
    
    if (isNaN(additional) || additional < 1 || additional > 10) {
      toast.info(t('misc.invitationsMustBe'));
      return;
    }

    try {
      const response = await api.post(`/api/admin/lenders/${userId}/add-invitations`, {
        additional_invitations: additional
      });
      
      toast.success(`Invitacions afegides correctament (${additional})`);
      fetchLenders(); // Refresh list
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error desconocido';
      toast.error(`Error:\n\n${errorMsg}`);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      surname: '',
      email: '',
      doc_type: 'DNI',
      doc_number: '',
      address_full: '',
      country: 'España',
      phone: '',
      iban: '',
      language_pref: 'es'
    });
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setEditingLender(null);
    resetForm();
  };

  const exportLendersCSV = () => {
    if (lenders.length === 0) {
      toast.info(t('messages.noDataToExport'));
      return;
    }

    const headers = ['Nombre,Apellidos,Email,Tipo Doc,Num Doc,Telefono,Direccion,Pais,IBAN,Idioma'];
    const rows = lenders.map(lender => {
      const profile = lender.profile || {};
      return `${lender.user.name},${lender.user.surname},${lender.user.email},${profile.doc_type || ''},${profile.doc_number || ''},${profile.phone || ''},${profile.address_full || ''},${profile.country || ''},${profile.iban || ''},${lender.user.language_pref || ''}`;
    });
    
    const csvContent = [headers, ...rows].join('\\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `prestamistas_vivendius_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(t('messages.csvExported'));
  };

  if (loading) {
    return (
      <div className="main-layout">
        <Sidebar />
        <div className="content-area" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-layout">
      <Sidebar />
      <div className="content-area">
        <TopBar 
          title={t('admin.lenders')} 
          actions={
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={exportLendersCSV}
                className="btn-secondary"
                data-testid="export-lenders-csv"
                style={{ padding: '0.75rem 1.5rem' }}
              >
                📊 {t('buttons.exportCSV')}
              </button>
              <button
                onClick={() => { resetForm(); setShowCreateModal(true); }}
                className="btn-primary"
                data-testid="create-lender-button"
                style={{ padding: '0.75rem 1.5rem' }}
              >
                + {t('admin.createLender')}
              </button>
            </div>
          }
        />

        <main style={{ padding: '2.5rem' }} className="fade-in">
          {/* Page Title with better spacing */}
          <div style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: 'rgba(255,255,255,0.95)', marginBottom: '0.5rem' }}>
              {t('admin.lenders')}
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem' }}>
              {t('admin.totalLenders')}: {lenders.length}
            </p>
          </div>
          
          {/* Search Bar */}
          <div className="dark-card" style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <input
                type="text"
                placeholder="🔍 Buscar por nombre, apellidos o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                data-testid="search-lenders-input"
                style={{ flex: 1 }}
              />
              <button
                onClick={handleSearch}
                className="btn-primary"
                data-testid="search-lenders-button"
                style={{ padding: '0.875rem 2rem' }}
              >
                Buscar
              </button>
            </div>
          </div>

          {/* Lenders Table */}
          <div className="dark-table" style={{ marginTop: '2rem' }}>
            <table style={{ width: '100%' }} data-testid="lenders-table">
              <thead>
                <tr>
                  <th>{t('admin.lenderName')}</th>
                  <th>{t('admin.lenderEmail')}</th>
                  <th>{t('admin.documentType')}</th>
                  <th>{t('admin.phone')}</th>
                  <th>{t('profile.userLanguage')}</th>
                  <th>{t('common.viewDetails')}</th>
                </tr>
              </thead>
              <tbody>
                {lenders.map((lender) => (
                  <tr key={lender.user._id} data-testid={`lender-row-${lender.user._id}`}>
                    <td data-label="Nombre" style={{ fontWeight: '600' }}>{lender.user.name} {lender.user.surname}</td>
                    <td data-label="Email" style={{ color: '#2563eb' }}>{lender.user.email}</td>
                    <td data-label="Documento" style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                      {lender.profile?.doc_type} {lender.profile?.doc_number || '-'}
                    </td>
                    <td data-label="Teléfono">{lender.profile?.phone || '-'}</td>
                    <td data-label="Idioma">
                      <span className="status-badge" style={{ background: '#dbeafe', color: '#1e40af' }}>
                        {lender.user.language_pref?.toUpperCase()}
                      </span>
                    </td>
                    <td data-label="Acciones">
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => openEditModal(lender)}
                          className="btn-secondary"
                          data-testid={`edit-lender-${lender.user._id}`}
                          style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                        >
                          ✏️ Editar
                        </button>
                        <button
                          onClick={() => handleAddInvitations(
                            lender.user._id, 
                            `${lender.user.name} ${lender.user.surname}`,
                            lender.user.invitations_available || 0,
                            lender.user.invitations_used || 0
                          )}
                          style={{
                            padding: '0.5rem 1rem',
                            fontSize: '0.875rem',
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                          data-testid={`add-invitations-${lender.user._id}`}
                        >
                          ➕ Invitacions ({lender.user.invitations_available || 0})
                        </button>
                        <button
                          onClick={() => handleResetPassword(lender.user._id, `${lender.user.name} ${lender.user.surname}`)}
                          style={{
                            padding: '0.5rem 1rem',
                            fontSize: '0.875rem',
                            background: 'linear-gradient(135deg, #fb923c 0%, #f97316 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                          data-testid={`reset-password-${lender.user._id}`}
                        >
                          🔑 Reset
                        </button>
                        <button
                          onClick={() => handleDeleteLender(lender.user._id, `${lender.user.name} ${lender.user.surname}`, lender.user.email)}
                          style={{
                            padding: '0.5rem 1rem',
                            fontSize: '0.875rem',
                            background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                          data-testid={`delete-lender-${lender.user._id}`}
                        >
                          🗑️ Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {lenders.length === 0 && (
            <div style={{ textAlign: 'center', padding: '4rem', color: '#9ca3af' }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>👥</div>
              <p style={{ fontSize: '1.25rem' }}>No hay prestamistas registrados</p>
            </div>
          )}
        </main>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="modal-content" style={{ maxWidth: '800px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.75rem', fontWeight: '700', color: '#103766', margin: 0 }}>
                {showEditModal ? t('modal.editLender') : t('modal.createLender')}
              </h3>
              <button
                onClick={closeModal}
                style={{
                  background: '#f3f4f6',
                  border: 'none',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#6b7280'
                }}
                data-testid="close-modal"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateLender} style={{ display: 'grid', gap: '1.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                    Nombre *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    data-testid="lender-name-input"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                    Apellidos *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.surname}
                    onChange={(e) => setFormData({...formData, surname: e.target.value})}
                    data-testid="lender-surname-input"
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  data-testid="lender-email-input"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                    Tipo de Documento *
                  </label>
                  <select
                    value={formData.doc_type}
                    onChange={(e) => setFormData({...formData, doc_type: e.target.value})}
                    data-testid="lender-doctype-input"
                  >
                    <option value="DNI">DNI</option>
                    <option value="NIE">NIE</option>
                    <option value="passport">Pasaporte</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                    Número de Documento *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.doc_number}
                    onChange={(e) => setFormData({...formData, doc_number: e.target.value})}
                    data-testid="lender-docnumber-input"
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                  Dirección Completa *
                </label>
                <input
                  type="text"
                  required
                  value={formData.address_full}
                  onChange={(e) => setFormData({...formData, address_full: e.target.value})}
                  placeholder="C/ Méndez Núñez, 8, 1º 2ª, 08003, Barcelona"
                  data-testid="lender-address-input"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                    Teléfono *
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+34 912 345 678"
                    data-testid="lender-phone-input"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                    País
                  </label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData({...formData, country: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                  IBAN *
                </label>
                <input
                  type="text"
                  value={formData.iban}
                  onChange={(e) => setFormData({...formData, iban: e.target.value})}
                  placeholder="ES91 2100 0418 4502 0005 1332"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                  Idioma Preferido
                </label>
                <select
                  value={formData.language_pref}
                  onChange={(e) => setFormData({...formData, language_pref: e.target.value})}
                >
                  <option value="es">Español</option>
                  <option value="ca">Català</option>
                </select>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb' }}>
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn-secondary"
                  data-testid="cancel-create-lender"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="btn-primary"
                  data-testid="submit-create-lender"
                >
                  {creating ? t('status.saving') : (showEditModal ? t('buttons.saveChanges') : t('buttons.createLenderButton'))}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminLenders;
